import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Save, ArrowLeft, User, Users, Briefcase, MapPin, Mail, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITUATIONS_FAMILIALES = [
  "Célibataire",
  "Marié(e)",
  "Conjoint(e) de fait",
  "Séparé(e)",
  "Divorcé(e)",
  "Veuf/Veuve",
];

const RESIDENCES = [
  "Citoyen",
  "Résident Permanent",
  "Résident Temporaire",
  "Demandeur d'asile",
  "Personne protégée",
];

const RESIDENCES_CONJOINT = [
  "Citoyen",
  "Résident Permanent",
  "Résident Temporaire",
  "Demandeur d'asile",
  "Personne protégée",
  "Non Résident",
];

const EMPLOI_OPTIONS = ["OUI", "NON"];

const ClientFormPage = ({ user, settings }) => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const isEditing = !!clientId;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    ville: "",
    email: "",
    situation_familiale: "",
    residence: "",
    emploi: "",
    conjoint: null,
    // Honoraires
    honoraires_convenus: "",
    preparation_gratuite: false,
    // New fields for treatment tracking
    type_traitement: "Présentiel",
    date_reception_dossier: "",
    delai_traitement: "",
    unite_delai: "jours",
    date_debut_traitement: "",
  });

  const needsSpouse =
    formData.situation_familiale === "Marié(e)" ||
    formData.situation_familiale === "Conjoint(e) de fait";

  useEffect(() => {
    if (isEditing) {
      fetchClient();
    }
  }, [clientId]);

  useEffect(() => {
    // Initialize spouse data when situation changes to married/common-law
    if (needsSpouse && !formData.conjoint) {
      setFormData((prev) => ({
        ...prev,
        conjoint: {
          nom: "",
          prenom: "",
          residence: "",
          emploi: "",
        },
      }));
    } else if (!needsSpouse && formData.conjoint) {
      setFormData((prev) => ({
        ...prev,
        conjoint: null,
      }));
    }
  }, [formData.situation_familiale]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}`, {
        withCredentials: true,
      });
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Client non trouvé");
      navigate("/clients");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSpouseChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      conjoint: {
        ...prev.conjoint,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        conjoint: needsSpouse ? formData.conjoint : null,
        // Ensure numeric fields are properly typed
        honoraires_convenus: parseFloat(formData.honoraires_convenus),
        delai_traitement: formData.delai_traitement ? parseInt(formData.delai_traitement) : null,
      };

      if (isEditing) {
        await axios.put(`${API}/clients/${clientId}`, dataToSend, {
          withCredentials: true,
        });
        toast.success("Client mis à jour avec succès");
      } else {
        const response = await axios.post(`${API}/clients`, dataToSend, {
          withCredentials: true,
        });
        console.log("Client created:", response.data);
        toast.success("Client créé avec succès");
      }
      // Small delay before navigation to ensure toast is shown
      setTimeout(() => {
        navigate("/clients");
      }, 500);
    } catch (error) {
      console.error("Error saving client:", error);
      setLoading(false);
      
      // Handle different error formats
      if (error.response) {
        const data = error.response.data;
        if (data?.detail) {
          // Pydantic validation errors come as an array
          if (Array.isArray(data.detail)) {
            const messages = data.detail.map(err => 
              typeof err === 'object' ? `${err.loc?.join(' > ') || 'Champ'}: ${err.msg}` : err
            ).join(', ');
            toast.error(messages || "Erreur de validation");
          } else if (typeof data.detail === 'object') {
            toast.error(data.detail.msg || JSON.stringify(data.detail));
          } else {
            toast.error(String(data.detail));
          }
        } else {
          toast.error("Erreur lors de l'enregistrement");
        }
      } else if (error.request) {
        toast.error("Erreur de connexion au serveur");
      } else {
        toast.error("Erreur inattendue");
      }
    }
  };

  if (fetching) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6 max-w-4xl" data-testid="client-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/clients")}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {isEditing ? "Modifier le client" : "Nouveau client"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditing
                ? `Modification du dossier ${formData.code_client || ""}`
                : "Créer un nouveau dossier client"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Principal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Requérant Principal
              </CardTitle>
              <CardDescription>
                Informations du contribuable principal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => handleChange("prenom", e.target.value)}
                    required
                    data-testid="input-prenom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => handleChange("nom", e.target.value)}
                    required
                    data-testid="input-nom"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone">Numéro de téléphone</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone || ""}
                    onChange={(e) => handleChange("telephone", e.target.value)}
                    placeholder="(000) 000-0000"
                    data-testid="input-telephone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ville">Ville *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="ville"
                      value={formData.ville}
                      onChange={(e) => handleChange("ville", e.target.value)}
                      required
                      className="pl-10"
                      data-testid="input-ville"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Adresse courriel *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                    className="pl-10"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Situation familiale *</Label>
                  <Select
                    value={formData.situation_familiale}
                    onValueChange={(value) =>
                      handleChange("situation_familiale", value)
                    }
                    required
                  >
                    <SelectTrigger data-testid="select-situation">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {SITUATIONS_FAMILIALES.map((situation) => (
                        <SelectItem key={situation} value={situation}>
                          {situation}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Résidence *</Label>
                  <Select
                    value={formData.residence}
                    onValueChange={(value) => handleChange("residence", value)}
                    required
                  >
                    <SelectTrigger data-testid="select-residence">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESIDENCES.map((residence) => (
                        <SelectItem key={residence} value={residence}>
                          {residence}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Emploi *</Label>
                  <Select
                    value={formData.emploi}
                    onValueChange={(value) => handleChange("emploi", value)}
                    required
                  >
                    <SelectTrigger data-testid="select-emploi">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOI_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Honoraires convenus */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Honoraires</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="honoraires_convenus">Honoraires convenus (à payer)</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="honoraires_convenus"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.honoraires_convenus}
                          onChange={(e) => handleChange("honoraires_convenus", e.target.value)}
                          placeholder="0.00"
                          className="pl-10 font-mono"
                          disabled={formData.preparation_gratuite}
                          data-testid="input-honoraires-convenus"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="preparation_gratuite"
                          checked={formData.preparation_gratuite || false}
                          onChange={(e) => {
                            handleChange("preparation_gratuite", e.target.checked);
                            if (e.target.checked) {
                              handleChange("honoraires_convenus", "0");
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                          data-testid="checkbox-gratuit"
                        />
                        <Label htmlFor="preparation_gratuite" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                          Préparation gratuite
                        </Label>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formData.preparation_gratuite 
                        ? "Préparation gratuite - aucun paiement requis"
                        : "Montant total des honoraires à payer par le client"
                      }
                    </p>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Treatment Type Section */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Type de traitement</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type de traitement *</Label>
                    <Select
                      value={formData.type_traitement}
                      onValueChange={(value) => handleChange("type_traitement", value)}
                      required
                    >
                      <SelectTrigger data-testid="select-type-traitement">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Présentiel">Présentiel</SelectItem>
                        <SelectItem value="En ligne">En ligne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.type_traitement === "Présentiel" && (
                    <div className="space-y-2">
                      <Label htmlFor="date_debut_traitement">Date de début du traitement</Label>
                      <Input
                        id="date_debut_traitement"
                        type="date"
                        value={formData.date_debut_traitement || ""}
                        onChange={(e) => handleChange("date_debut_traitement", e.target.value)}
                        data-testid="input-date-debut"
                      />
                    </div>
                  )}

                  {formData.type_traitement === "En ligne" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="date_reception_dossier">Date de réception du dossier *</Label>
                        <Input
                          id="date_reception_dossier"
                          type="date"
                          value={formData.date_reception_dossier || ""}
                          onChange={(e) => handleChange("date_reception_dossier", e.target.value)}
                          required
                          data-testid="input-date-reception"
                        />
                      </div>
                    </>
                  )}
                </div>

                {formData.type_traitement === "En ligne" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="delai_traitement">Délai estimé de traitement *</Label>
                      <Input
                        id="delai_traitement"
                        type="number"
                        min="1"
                        value={formData.delai_traitement || ""}
                        onChange={(e) => handleChange("delai_traitement", parseInt(e.target.value) || "")}
                        required
                        placeholder="Ex: 5"
                        data-testid="input-delai-traitement"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unité du délai *</Label>
                      <Select
                        value={formData.unite_delai || "jours"}
                        onValueChange={(value) => handleChange("unite_delai", value)}
                        required
                      >
                        <SelectTrigger data-testid="select-unite-delai">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="jours">Jours</SelectItem>
                          <SelectItem value="semaines">Semaines</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Spouse Information */}
          {needsSpouse && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Conjoint(e)
                </CardTitle>
                <CardDescription>
                  Informations du/de la conjoint(e)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="conjoint_prenom">Prénom *</Label>
                    <Input
                      id="conjoint_prenom"
                      value={formData.conjoint?.prenom || ""}
                      onChange={(e) =>
                        handleSpouseChange("prenom", e.target.value)
                      }
                      required
                      data-testid="input-conjoint-prenom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conjoint_nom">Nom *</Label>
                    <Input
                      id="conjoint_nom"
                      value={formData.conjoint?.nom || ""}
                      onChange={(e) => handleSpouseChange("nom", e.target.value)}
                      required
                      data-testid="input-conjoint-nom"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Résidence *</Label>
                    <Select
                      value={formData.conjoint?.residence || ""}
                      onValueChange={(value) =>
                        handleSpouseChange("residence", value)
                      }
                      required
                    >
                      <SelectTrigger data-testid="select-conjoint-residence">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {RESIDENCES_CONJOINT.map((residence) => (
                          <SelectItem key={residence} value={residence}>
                            {residence}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.conjoint?.residence !== "Non Résident" && (
                    <div className="space-y-2">
                      <Label>Emploi *</Label>
                      <Select
                        value={formData.conjoint?.emploi || ""}
                        onValueChange={(value) =>
                          handleSpouseChange("emploi", value)
                        }
                        required
                      >
                        <SelectTrigger data-testid="select-conjoint-emploi">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOI_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/clients")}
              data-testid="cancel-button"
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-button">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? "Mettre à jour" : "Créer le client"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default ClientFormPage;
